// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';

import {loadStripe} from '@stripe/stripe-js';
import {Elements, useElements} from '@stripe/react-stripe-js';

import Box from 'sail/Box';
import Group from 'sail/Group';
import {Text, Title, Intro} from 'sail/Text';

import Card from 'sail/Card';

import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';

import {CodeBlock} from 'docs/components';
import {useAccount} from 'docs/data';

import './AfterpayMessagingDemo.scss';

type AfterpayMessagingProps = {|
  options?: any,
  hideLogo?: boolean,
  hideText?: boolean,
  hideModal?: boolean,
|};

const AfterpayMessaging = ({
  options,
  hideLogo,
  hideText,
  hideModal,
}: AfterpayMessagingProps) => {
  const elements = useElements();
  const ref = React.useRef();

  useEffect(() => {
    if (!elements) {
      // Wait for Stripe.js to load
      return;
    }

    const afterpayClearpayMessage = elements.create('afterpayClearpayMessage', {
      amount: 3000,
      currency: 'USD',
      ...options,
    });
    afterpayClearpayMessage.mount(ref.current);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [elements]);

  let className = 'AfterpayMessageWrapper';
  if (hideText) {
    className += ' hide-text';
  }

  if (hideModal) {
    className += ' hide-modal';
  }

  return <div className={className} ref={ref} />;
};

const ProductDemo = () => {
  return (
    <Box
      className="DocsDemo"
      background="offset"
      margin={{vertical: 20}}
      padding={20}
      flex={{direction: 'row'}}
    >
      <Box padding={{right: 12}}>
        <div className="DocsDemo-PrinceOfPersiaImage" />
      </Box>
      <Group direction="vertical" spacing={8}>
        <Title className="title">
          The Making of Prince of Persia: Journals 1985-1993
        </Title>
        <Intro>Jordan Mechner</Intro>
        <Box background="white" flex={{direction: 'column'}} padding={12}>
          <Title>$30</Title>
          <AfterpayMessaging />
        </Box>
      </Group>
    </Box>
  );
};

const BadgeDemo = () => {
  return (
    <DataTable>
      <DataTableHead
        columns={[
          {label: 'black-on-mint'},
          {label: 'black-on-white'},
          {label: 'mint-on-black'},
          {label: 'white-on-black'},
        ]}
      />
      <DataTableBody>
        <DataTableRow>
          <DataTableCell align="center">
            <AfterpayMessaging
              options={{badgeTheme: 'black-on-mint'}}
              hideText
              hideModal
            />
          </DataTableCell>
          <DataTableCell align="center" className="background-gray">
            <AfterpayMessaging
              options={{badgeTheme: 'black-on-white'}}
              hideText
              hideModal
            />
          </DataTableCell>
          <DataTableCell align="center">
            <AfterpayMessaging
              options={{badgeTheme: 'mint-on-black'}}
              hideText
              hideModal
            />
          </DataTableCell>
          <DataTableCell align="center">
            <AfterpayMessaging
              options={{badgeTheme: 'white-on-black'}}
              hideText
              hideModal
            />
          </DataTableCell>
        </DataTableRow>
      </DataTableBody>
    </DataTable>
  );
};

const LockupDemo = () => (
  <DataTable>
    <DataTableHead
      columns={[{label: 'black'}, {label: 'white'}, {label: 'mint'}]}
    />
    <DataTableBody>
      <DataTableRow>
        <DataTableCell align="center">
          <AfterpayMessaging
            options={{logoType: 'lockup', lockupTheme: 'black'}}
            hideText
            hideModal
          />
        </DataTableCell>
        <DataTableCell align="center" className="background-gray">
          <AfterpayMessaging
            options={{logoType: 'lockup', lockupTheme: 'white'}}
            hideText
            hideModal
          />
        </DataTableCell>
        <DataTableCell
          align="center"
          className="background-gray background-gray-800"
        >
          <AfterpayMessaging
            options={{logoType: 'lockup', lockupTheme: 'mint'}}
            hideText
            hideModal
          />
        </DataTableCell>
      </DataTableRow>
    </DataTableBody>
  </DataTable>
);

const CodeAndPreview = ({preview, codeBlock, label}) => {
  return (
    <Box margin={{top: 24, bottom: 24}}>
      <Box>
        <Card radius="top" shadow="keyline">
          <Box padding={{all: 20}}>{preview}</Box>
        </Card>
      </Box>
      <Box
        padding={{vertical: 4, horizontal: 12}}
        background="gray50"
        divider={{color: 'light', width: 1, side: 'all'}}
        style={{
          margin: '0 -1px',
        }}
      >
        <Text color="gray" weight="medium">
          {label}
        </Text>
      </Box>
      <Box className="CodeContainer">{codeBlock}</Box>
    </Box>
  );
};

const CSSDemoMessage = () => (
  <div>
    <CodeAndPreview
      label="CSS"
      preview={
        <Box className="blue-monospace">
          <AfterpayMessaging />
        </Box>
      }
      codeBlock={
        <CodeBlock language="css">{`.AfterpayMessage {
  font-family: monospace;
  font-size: 14px;
  color: blue;
}`}</CodeBlock>
      }
    />
  </div>
);

const CSSDemoLogo = () => (
  <div>
    <CodeAndPreview
      label="CSS"
      preview={
        <Box className="logo-size">
          <AfterpayMessaging />
        </Box>
      }
      codeBlock={
        <CodeBlock language="css">{`.AfterpayMessage-logoSvg {
  width: 80px;
  height: auto;
}`}</CodeBlock>
      }
    />
  </div>
);

const LimitsDemo = () => {
  return (
    <DataTable>
      <DataTableBody>
        <DataTableRow>
          <DataTableCell vAlign="middle">
            <span className="CodeInline">(default)</span>
          </DataTableCell>
          <DataTableCell vAlign="middle">
            <AfterpayMessaging options={{amount: 20000000}} />
          </DataTableCell>
        </DataTableRow>
        <DataTableRow>
          <DataTableCell vAlign="middle">
            <span className="CodeInline">showLowerLimit: false</span>
          </DataTableCell>
          <DataTableCell vAlign="middle">
            <AfterpayMessaging
              options={{amount: 20000000, showLowerLimit: false}}
            />
          </DataTableCell>
        </DataTableRow>
        <DataTableRow>
          <DataTableCell vAlign="middle">
            <span className="CodeInline">showUpperLimit: false</span>
          </DataTableCell>
          <DataTableCell vAlign="middle">
            <AfterpayMessaging
              options={{amount: 20000000, showUpperLimit: false}}
            />
          </DataTableCell>
        </DataTableRow>
      </DataTableBody>
    </DataTable>
  );
};

const EligibleDemo = () => {
  return (
    <DataTable>
      <DataTableBody>
        <DataTableRow>
          <DataTableCell vAlign="middle">
            <span className="CodeInline">isEligible: false</span>
          </DataTableCell>
          <DataTableCell vAlign="middle">
            <AfterpayMessaging options={{isEligible: false}} />
          </DataTableCell>
        </DataTableRow>
        <DataTableRow>
          <DataTableCell vAlign="middle">
            <span className="CodeInline">isCartEligible: false</span>
          </DataTableCell>
          <DataTableCell vAlign="middle">
            <AfterpayMessaging options={{isCartEligible: false}} />
          </DataTableCell>
        </DataTableRow>
      </DataTableBody>
    </DataTable>
  );
};

const LocaleDemo = () => {
  return (
    <div>
      <CodeAndPreview
        label="JavaScript"
        preview={
          <Box>
            <AfterpayMessaging options={{amount: 1000, currency: 'GBP'}} />
          </Box>
        }
        codeBlock={
          <CodeBlock language="javascript">{`const elements = stripe.elements({
  locale: 'en-GB'
});

const options = {
  amount: 1000, // £10.00
  currency: 'GBP'
};

const afterpayClearpayMessageElement =
  elements.create('afterpayClearpayMessage', options);

afterpayClearpayMessageElement.mount('#afterpay-clearpay-message');`}</CodeBlock>
        }
      />
    </div>
  );
};

const LocaleReactDemo = () => {
  return (
    <div>
      <CodeAndPreview
        label="React"
        preview={
          <Box>
            <AfterpayMessaging options={{amount: 1000, currency: 'GBP'}} />
          </Box>
        }
        codeBlock={
          <CodeBlock language="jsx">{`const ProductDetail = () => (
  <div>
    {/* ... */}
    <Elements stripe={stripePromise} options={{locale: 'en-GB'}}>
      <AfterpayClearpayMessageElement options={{amount: 1000, currency: 'GBP'}} />
    </Elements>
  </div>
);`}</CodeBlock>
        }
      />
    </div>
  );
};

export const AfterpayMessagingDemo = ({part}: {part: string}) => {
  const {account} = useAccount();
  const [promise, setPromise] = useState(null);

  useEffect(() => {
    setPromise(loadStripe(account.keys.publishable));
  }, [account.keys.publishable]);

  let partComponent;
  let locale = 'en-US';

  if (part === 'product') {
    partComponent = <ProductDemo />;
  } else if (part === 'badge') {
    partComponent = <BadgeDemo />;
  } else if (part === 'lockup') {
    partComponent = <LockupDemo />;
  } else if (part === 'css-demo-message') {
    partComponent = <CSSDemoMessage />;
  } else if (part === 'css-demo-logo') {
    partComponent = <CSSDemoLogo />;
  } else if (part === 'limits') {
    partComponent = <LimitsDemo />;
  } else if (part === 'eligible') {
    partComponent = <EligibleDemo />;
  } else if (part === 'locale') {
    locale = 'en-GB';
    partComponent = <LocaleDemo />;
  } else if (part === 'locale-react') {
    locale = 'en-GB';
    partComponent = <LocaleReactDemo />;
  }

  return (
    <Box className="AfterpayMessagingDemo" margin={{top: 12}}>
      <Elements stripe={promise} options={{locale}}>
        {partComponent}
      </Elements>
    </Box>
  );
};
